/**
 * White-label SEO token fill.
 *
 * SEO metadata is written generically with tokens so each client inherits their
 * own identity (see CLAUDE.md → Conventions):
 *   {business} → businessInfo.businessName
 *   {domain}   → bare host of businessInfo.website (no protocol / trailing slash)
 *
 * Used by the shared <Seo> component and by any screen that sets `document.title`
 * directly (and therefore can't go through <Layout>).
 */

// Strip protocol + trailing slash → bare host ("https://x.com/" → "x.com").
export function bareHost(website = '') {
    return String(website || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
}

// Replace {business}/{domain} tokens; tidy a dangling leading/trailing separator
// when a token resolves to empty so titles never render " | Foo".
export function fillSeoTokens(input, { businessName = '', website = '' } = {}) {
    if (typeof input !== 'string') return input;
    const domain = bareHost(website);
    return input
        .replace(/\{\{?\s*business(?:Name)?\s*\}?\}/gi, businessName)
        .replace(/\{\{?\s*domain\s*\}?\}/gi, domain)
        .replace(/^\s*[|\-–—]\s*/, '')
        .replace(/\s*[|\-–—]\s*$/, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
}

export function resizeImageToBase64(file, maxWidth = 800, maxHeight = 800) {
    return new Promise((resolve) => {
        const img = new window.Image();
        const reader = new window.FileReader();
        reader.onload = (e) => {
            img.onload = () => {
                let { width, height } = img;
                const aspectRatio = width / height;

                if (width > maxWidth || height > maxHeight) {
                    if (aspectRatio > 1) {
                        width = Math.min(width, maxWidth);
                        height = width / aspectRatio;
                    } else {
                        height = Math.min(height, maxHeight);
                        width = height * aspectRatio;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL('image/png'));
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    });
};